import { Alert } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { TbArrowsMaximize, TbArrowsMinimize, TbCalendar } from "react-icons/tb";

import FilterButton from "@/components/table/FilterButton";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import { WelcomeTeamTitle } from "@/pages/WelcomeTeam/components/WelcomeTeamTitle";
import useDriversContext from "@/providers/data/DriversContext";
import {
    useTrackingActions,
    useTrackingDate,
    useTrackingFilters,
    useTrackingIsFullScreen,
} from "@/stores/use-tracking-table";
import { IPlanPassenger, Task } from "@/types/tasks.types";

import MobileTrackingSkeleton from "./MobileTrackingSkeleton";
import TrackingTaskCard from "./TrackingTaskCard";

export type MobileTrackingDashboardProps = {
    rows: Task<IPlanPassenger<"api">>[];
    loading: boolean;
};

function MobileTrackingDashboard({ rows, loading }: MobileTrackingDashboardProps) {
    const { t } = useTranslation();
    const { drivers } = useDriversContext();
    const isFullScreen = useTrackingIsFullScreen();
    const { size } = useWindowDimensions();
    const date = useTrackingDate();

    const { updateFullScreen, setDate, updateFilters } = useTrackingActions();
    const filters = useTrackingFilters();
    return loading && !rows.length ? (
        <MobileTrackingSkeleton />
    ) : (
        <>
            <motion.div
                initial={{ height: "100%", position: "static" }}
                animate={{
                    height: isFullScreen ? "100vh" : "auto",
                    width: isFullScreen ? "100vw" : "100%",
                    position: isFullScreen ? "fixed" : "relative",
                    top: 0,
                    left: 0,
                    background: isFullScreen ? "white" : "transparent", // Ensure it's visible in fullscreen
                    zIndex: isFullScreen ? 1000 : "auto",
                    padding: isFullScreen ? (size.md ? 16 : 4) : 0,
                }}
                transition={{ duration: 0.3 }}
                className={isFullScreen ? "overflow-auto" : "overflow-inherit"}
            >
                <div className=" flex items-center justify-end gap-2 sticky top-0 left-0 w-full z-50 bg-white">
                    <WelcomeTeamTitle search={filters.search} setSearch={(text) => updateFilters({ search: text })} />
                    <DatePicker
                        className="!w-36 grow-0 shrink-0"
                        value={date}
                        onChange={(date: DateTime | null) => {
                            if (date) setDate(date);
                        }}
                        slotProps={{
                            textField: {
                                InputProps: {
                                    endAdornment: (
                                        <TbCalendar
                                            size={25}
                                            color="gray"
                                            className=" -translate-y-[1px] text-gray-400"
                                        />
                                    ),
                                },
                            },
                        }}
                    />
                    <FilterButton
                        handleClick={() => updateFullScreen()}
                        active={isFullScreen}
                        tooltip={t("tooltips.fulscreen", { count: isFullScreen ? 2 : 1 })}
                        icon={isFullScreen ? TbArrowsMinimize : TbArrowsMaximize}
                    />
                </div>

                <div className="flex flex-col gap-4 mt-1   grow-0 shrink-0 mb-10 ">
                    {rows.length ? (
                        rows.map((task) => (
                            <div key={`tracking-card-${task.id}`}>
                                <TrackingTaskCard drivers={drivers} task={task} />
                            </div>
                        ))
                    ) : (
                        <Alert severity="warning">{t("no_tasks_found")}</Alert>
                    )}
                </div>
            </motion.div>
        </>
    );
}

export default MobileTrackingDashboard;
